import { parseNumber, type ParsedNumber } from 'libphonenumber-js';
import { phoneNumberFormatter } from '@/utils/auth';

export const validatePhoneNumber = (value: string): boolean => {
  if (/^[\d]+$/i.test(value)) {
    const parsedNumber: {} | ParsedNumber = parseNumber(`+1${value}`, 'US');

    if ('phone' in parsedNumber) {
      return true;
    }
  }

  return false;
};

export const formatPhoneNumber = (phoneNumber: string) =>
  `(${phoneNumber?.slice(0, 3)}) ${phoneNumber?.slice(3, 6)}-${phoneNumber?.slice(6)}`;

export const sanitizePhoneNumber = (phoneNumber?: string | null): string => {
  const digits = phoneNumber?.replace(/\D/g, '') ?? '';

  if (digits.length === 10) {
    return digits;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }

  return '';
};

export const toSmsHref = (value?: string | null): string | null => {
  const digits = value?.replace(/\D/g, '') ?? '';

  if (digits.length === 10) {
    return `sms:+1${digits}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return `sms:+${digits}`;
  }

  return null;
};

/**
 * The `tel:` target for a stored phone string: a leading `+` if there is one, then digits only.
 * An "ext" / "x" suffix and everything after it is dropped — a dialler cannot use it. `null` when
 * no digits are left, which callers treat exactly as no phone on file.
 */
export const toTelHref = (phone: string): string | null => {
  const number = phone.replace(/(?:ext|x).*$/i, '').trim();
  const digits = number.replace(/\D/g, '');
  if (!digits) {
    return null;
  }
  return `tel:${number.startsWith('+') ? '+' : ''}${digits}`;
};

/**
 * A stored location phone as something a diner can call: the `tel:` target ({@link toTelHref})
 * and the text to show — formatted when it is a 10-digit number, otherwise as stored. `null` for
 * a missing, blank or digit-less value, which callers treat exactly as no phone on file.
 */
export const toCallablePhone = (
  phone?: string | null,
): { href: string; display: string } | null => {
  const stored = phone?.trim();
  const href = stored ? toTelHref(stored) : null;
  if (!stored || !href) {
    return null;
  }
  return { href, display: phoneNumberFormatter(stored) || stored };
};
